import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { hasAdminPrivileges } from '../../utils/permissionsClient';
import Icon from './icons';

const NAV_ITEMS = [
    ['Overview', Icon.home, '/dashboard'],
    ['All projects', Icon.folder, '/dashboard/projects'],
    ['New project', Icon.plus, '/dashboard/new-translation'],
    ['Inbox', Icon.inbox, '/dashboard/inbox'],
    ['Reports', Icon.chart, '/dashboard/analytics'],
    ['AI quality', Icon.gauge, '/dashboard/analytics'],
    ['Translation memory', Icon.memory, '/dashboard/glossary'],
    ['Glossaries', Icon.glossary, '/dashboard/glossary'],
    ['Brand voice', Icon.voice, '/dashboard/language-settings'],
    ['Integrations', Icon.link, '/dashboard/apps'],
    ['People', Icon.people, '/dashboard/team'],
    ['Settings', Icon.gear, '/dashboard/account-settings'],
    ['Account', Icon.user, '/dashboard/profile'],
];

const ACTIONS = [
    ['New project', Icon.plus, '/dashboard/new-translation'],
    ['Invite people', Icon.people, '/dashboard/team'],
    ['Manage integrations', Icon.link, '/dashboard/apps'],
    ['Help & support', Icon.help, '/dashboard/support'],
];

const CommandPalette = ({ onClose }) => {
    const [q, setQ] = useState('');
    const [sel, setSel] = useState(0);
    const inputRef = useRef(null);
    const navigate = useNavigate();
    const user = useSelector((s) => s.auth.user) || {};
    const isAdmin = hasAdminPrivileges(user);

    const all = useMemo(() => {
        const items = NAV_ITEMS.map(([l, ic, href]) => ({ sec: 'Go to', label: l, icon: ic, href, kind: 'Page' }));
        if (isAdmin) items.push({ sec: 'Go to', label: 'Admin', icon: Icon.shield, href: '/dashboard/admin', kind: 'Page' });
        ACTIONS.forEach(([l, ic, href]) => items.push({ sec: 'Actions', label: l, icon: ic, href, kind: 'Action' }));
        return items;
    }, [isAdmin]);

    const filtered = useMemo(() => {
        const s = q.trim().toLowerCase();
        if (!s) return all;
        return all.filter((i) => i.label.toLowerCase().includes(s) || i.kind.toLowerCase().includes(s));
    }, [q, all]);

    const groups = useMemo(() => {
        const order = ['Go to', 'Actions'];
        const map = {};
        filtered.forEach((i) => {
            (map[i.sec] = map[i.sec] || []).push(i);
        });
        return order.filter((o) => map[o]).map((o) => [o, map[o]]);
    }, [filtered]);
    const flat = useMemo(() => groups.flatMap(([, arr]) => arr), [groups]);

    useEffect(() => setSel(0), [q]);
    useEffect(() => {
        if (inputRef.current) inputRef.current.focus();
    }, []);

    const activate = (it) => {
        if (it && it.href) navigate(it.href);
        onClose();
    };

    const onKey = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSel((s) => Math.min(flat.length - 1, s + 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSel((s) => Math.max(0, s - 1));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            activate(flat[sel]);
        } else if (e.key === 'Escape') {
            e.preventDefault();
            onClose();
        }
    };

    let idx = -1;
    return (
        <div className="cmdk-overlay" onMouseDown={(e) => { if (e.target.classList.contains('cmdk-overlay')) onClose(); }}>
            <div className="cmdk" role="dialog" aria-label="Quick find">
                <div className="cmdk-search">
                    {Icon.search}
                    <input
                        ref={inputRef}
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        onKeyDown={onKey}
                        placeholder="Search pages or jump to a screen…"
                    />
                    <span className="cmdk-esc">esc</span>
                </div>
                <div className="cmdk-results">
                    {groups.length === 0 && <div className="cmdk-empty">No results for &ldquo;{q}&rdquo;.</div>}
                    {groups.map(([sec, arr]) => (
                        <div className="cmdk-group" key={sec}>
                            <div className="cmdk-sec">{sec}</div>
                            {arr.map((it) => {
                                idx += 1;
                                const i = idx;
                                const on = i === sel;
                                return (
                                    <div
                                        key={it.sec + it.label}
                                        className={`cmdk-item ${on ? 'on' : ''}`}
                                        onMouseEnter={() => setSel(i)}
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            activate(it);
                                        }}
                                    >
                                        <span className="cmdk-ic">{it.icon}</span>
                                        <span className="cmdk-label">{it.label}</span>
                                        <span className="cmdk-kind">{it.kind}</span>
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
                <div className="cmdk-foot">
                    <span><span className="k">↑</span><span className="k">↓</span> navigate</span>
                    <span><span className="k">↵</span> open</span>
                    <span><span className="k">esc</span> close</span>
                </div>
            </div>
        </div>
    );
};

export default CommandPalette;
